import React from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  findDependentItemTitles,
  describeDependents,
} from "./dependency-utils";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  HiringProcessAssigneeRole,
  HiringProcessItemType,
} from "@api/shared-runtime/enums";
import type { LocalFile } from "@/components/file-upload/local-file-input";
import { STAGE_COLORS, STAGE_PILL_COLORS } from "./constants";
import { ItemRow } from "./item-row";
import type {
  ExistingAttachmentsMap,
  HiringProcessTemplateFormValues,
} from "./schema";

interface StageSectionProps {
  stageIndex: number;
  totalStages: number;
  form: ReturnType<typeof useForm<HiringProcessTemplateFormValues>>;
  availableForms: { id: string; name: string }[];
  onRemoveStage: (index: number) => void;
  /** Called with the DB id when an existing item is removed via the trash button. */
  onRecordRemovedItemId: (itemId: string) => void;
  onMoveStageUp: (index: number) => void;
  onMoveStageDown: (index: number) => void;
  localFilesRef: React.MutableRefObject<Map<string, LocalFile[]>>;
  existingAttachments?: ExistingAttachmentsMap;
  docShareErrorKeys: Set<string>;
  enableDocumentSigning: boolean;
  onCreateForm: (stageIndex: number, itemIndex: number) => void;
  onEditFormSpec: (stageIndex: number, itemIndex: number) => void;
}

export function StageSection({
  stageIndex,
  totalStages,
  form,
  availableForms,
  onRemoveStage,
  onRecordRemovedItemId,
  onMoveStageUp,
  onMoveStageDown,
  localFilesRef,
  existingAttachments,
  docShareErrorKeys,
  enableDocumentSigning,
  onCreateForm,
  onEditFormSpec,
}: StageSectionProps) {
  const {
    fields: itemFields,
    append: appendItem,
    remove: removeItem,
    move: moveItem,
  } = useFieldArray({
    control: form.control,
    name: `stages.${stageIndex}.items`,
  });

  const colorBorder =
    STAGE_COLORS[stageIndex % STAGE_COLORS.length] ?? "border-l-blue-400";
  const colorPill =
    STAGE_PILL_COLORS[stageIndex % STAGE_PILL_COLORS.length] ??
    "bg-blue-100 text-blue-700";

  const handleAddItem = () => {
    appendItem({
      // Client-generated ref so predecessors can point at this item before
      // it gets a server-assigned id on save.
      ref: `client-${crypto.randomUUID()}`,
      title: "",
      description: "",
      sortOrder: itemFields.length,
      isRequired: true,
      assigneeRole: HiringProcessAssigneeRole.Provider,
      // No type chosen yet — the editor hides the assignee/required controls
      // until the author picks one. (Left as Task on save if untouched.)
      itemType: undefined as unknown as HiringProcessItemType,
      formId: null,
      richTextContent: null,
      fileUploadIds: [],
      links: [],
      predecessors: [],
    });
  };

  const handleRemoveItem = (itemIndex: number) => {
    const item = form.getValues(`stages.${stageIndex}.items.${itemIndex}`);
    // Block removal while other steps still depend on this one — the user must
    // clear the dependency first, otherwise the save references a step that no
    // longer exists.
    if (item.ref) {
      const dependents = findDependentItemTitles(
        form.getValues("stages"),
        new Set([item.ref]),
      );
      if (dependents.length > 0) {
        toast.error(
          `Can't remove “${
            item.title?.trim() || "this step"
          }” — ${describeDependents(dependents)} depend${
            dependents.length === 1 ? "s" : ""
          } on it. Remove that dependency first.`,
        );
        return;
      }
    }
    if (item.id) {
      onRecordRemovedItemId(item.id);
    }
    removeItem(itemIndex);
  };

  const handleMoveItemUp = (itemIndex: number) => {
    if (itemIndex === 0) {
      return;
    }
    moveItem(itemIndex, itemIndex - 1);
    const items = form.getValues(`stages.${stageIndex}.items`);
    items.forEach((_, i) => {
      form.setValue(`stages.${stageIndex}.items.${i}.sortOrder`, i);
    });
  };

  const handleMoveItemDown = (itemIndex: number) => {
    if (itemIndex === itemFields.length - 1) {
      return;
    }
    moveItem(itemIndex, itemIndex + 1);
    const items = form.getValues(`stages.${stageIndex}.items`);
    items.forEach((_, i) => {
      form.setValue(`stages.${stageIndex}.items.${i}.sortOrder`, i);
    });
  };

  return (
    <div
      className={`border border-gray-200 border-l-4 ${colorBorder} rounded-lg overflow-hidden`}
    >
      {/* Stage Header */}
      <div className="bg-gray-50 border-b border-gray-200 px-2 sm:px-3 py-2.5 flex items-center gap-1.5 sm:gap-2">
        {/* Stage number pill */}
        <span
          className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${colorPill}`}
        >
          {stageIndex + 1}
        </span>

        {/* Stage name input */}
        <FormField
          control={form.control}
          name={`stages.${stageIndex}.name`}
          render={({ field }) => (
            <FormItem className="flex-1 min-w-0">
              <FormControl>
                <Input
                  placeholder="Stage name"
                  className="h-7 py-0.5 text-sm font-medium border-transparent bg-transparent hover:bg-white hover:border-gray-200 focus:bg-white focus:border-gray-300 transition-colors px-2"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Stage description - compact inline */}
        <FormField
          control={form.control}
          name={`stages.${stageIndex}.description`}
          render={({ field }) => (
            <FormItem className="hidden sm:block w-44 shrink-0">
              <FormControl>
                <Input
                  placeholder="Description (optional)"
                  className="h-7 py-0.5 text-xs text-gray-400 border-transparent bg-transparent hover:bg-white hover:border-gray-200 focus:bg-white focus:border-gray-300 transition-colors px-2"
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
            </FormItem>
          )}
        />

        {/* Item count badge */}
        <span className="shrink-0 text-xs text-gray-400 tabular-nums">
          {itemFields.length} item{itemFields.length !== 1 ? "s" : ""}
        </span>

        {/* Reorder buttons */}
        <div className="flex gap-0.5 shrink-0">
          <button
            type="button"
            onClick={() => onMoveStageUp(stageIndex)}
            disabled={stageIndex === 0}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-25 disabled:cursor-not-allowed p-1.5 rounded hover:bg-gray-200 transition-colors"
            aria-label="Move stage up"
            title="Move stage up"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="2,8 6,4 10,8" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => onMoveStageDown(stageIndex)}
            disabled={stageIndex === totalStages - 1}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-25 disabled:cursor-not-allowed p-1.5 rounded hover:bg-gray-200 transition-colors"
            aria-label="Move stage down"
            title="Move stage down"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="2,4 6,8 10,4" />
            </svg>
          </button>
        </div>

        {/* Remove stage */}
        <button
          type="button"
          onClick={() => onRemoveStage(stageIndex)}
          disabled={totalStages === 1}
          className="text-red-300 hover:text-red-500 disabled:opacity-25 disabled:cursor-not-allowed shrink-0 p-1.5 rounded hover:bg-red-50 transition-colors"
          aria-label="Remove stage"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Stage Items */}
      <div className="px-2 sm:px-3 py-2.5 space-y-1.5 bg-white">
        {itemFields.length === 0 && (
          <p className="text-xs text-gray-400 italic text-center py-2">
            No items in this stage yet.
          </p>
        )}

        {itemFields.map((itemField, itemIndex) => (
          <ItemRow
            key={itemField.id}
            stageIndex={stageIndex}
            itemIndex={itemIndex}
            totalItems={itemFields.length}
            form={form}
            availableForms={availableForms}
            onRemove={handleRemoveItem}
            onMoveUp={handleMoveItemUp}
            onMoveDown={handleMoveItemDown}
            localFilesRef={localFilesRef}
            existingAttachments={existingAttachments}
            onCreateForm={onCreateForm}
            onEditFormSpec={onEditFormSpec}
            enableDocumentSigning={enableDocumentSigning}
            hasDocShareError={docShareErrorKeys.has(
              `${stageIndex}-${itemIndex}`,
            )}
          />
        ))}

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full border-dashed mt-1 h-7 text-xs text-gray-500 hover:text-gray-700"
          onClick={handleAddItem}
        >
          <Plus size={12} className="mr-1" />
          Add Item
        </Button>
      </div>
    </div>
  );
}
